import type { DTO, Nullable } from '@/type-utils';
import { isNullish } from '@/utils/null-utils';
import { isTokenExpired } from '@/utils/auth-utils/token-utils';
import BaseUserModel from '../BaseUserModel';
import UserType from '../UserType';
import IGuestUser from './IGuestUser';
import type { IGuestUserTokens } from '../IUserTokens';

/**
 * Represents a {@link IGuestUser Guest User}.
 * Can optionally hold tokens for guest operations.
 */
export default class GuestUserModel
  extends BaseUserModel<DTO<IGuestUser>>
  implements IGuestUser
{
  /** @inheritdoc */
  public override readonly type = UserType.Guest;

  /**
   * Tokens for anonymous operations.
   * Initialized from DTO if provided.
   */
  private _tokens?: Nullable<IGuestUserTokens>;

  /**
   * Constructs a Guest User Model.
   * @param user - Guest user DTO with optional tokens.
   */
  public constructor(user: DTO<IGuestUser>) {
    super(user);
    this._tokens = user.tokens;
  }

  /**
   * Gets tokens if available.
   * @returns Guest user tokens or undefined if not present.
   */
  public get tokens(): Nullable<IGuestUserTokens> {
    return this._tokens;
  }

  /**
   * Checks if user has active tokens.
   * @returns True if tokens are present, false otherwise.
   */
  public get hasTokens(): boolean {
    return !isNullish(this._tokens);
  }

  /**
   * Checks if tokens are expired.
   * @returns True if tokens are missing or expired, false if valid.
   */
  public get areTokensExpired(): boolean {
    if (!this.hasTokens || !this._tokens) {
      return true;
    }

    return isTokenExpired(this._tokens);
  }

  /**
   * Sets or updates tokens.
   * Used when obtaining new tokens or refreshing existing ones.
   * @param tokens - New guest tokens to set.
   */
  public setTokens(tokens: IGuestUserTokens): void {
    this._tokens = tokens;
  }

  /**
   * Clears tokens.
   * Used when logging in as a registered user or explicitly clearing session.
   */
  public clearTokens(): void {
    this._tokens = undefined;
  }

  /**
   * Converts the model to a DTO for serialization.
   * @returns Guest user DTO with tokens if present.
   */
  public override toDTO(): DTO<IGuestUser> {
    return { ...super.toDTO(), tokens: this._tokens } as DTO<IGuestUser>;
  }
}
